import React, { ReactNode, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { BsFileEarmark } from "react-icons/bs";
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import { AnexoType } from "types/anexo/AnexoType";
interface CloudAnexoProps {
  onAddAnexo: (anexo: File) => void;
  onDownload?: (anexo: AnexoType) => void;
  onRemoveAnexo: (anexo: AnexoType) => void;
  limiteDeAnexos?: number;
  isVisualizacao?: boolean;
  anexos: AnexoType[];
  tamanhoMaximoEmMegaByteDoAnexo?: number;
  /**As extensões passadas aqui irão ser previamente filtradas na maquina do usuário*/
  accept?: string;
  extensoesAceitas?: string[];
  mensagemDeErro?: string;
}

const CloudAnexo: React.FC<CloudAnexoProps> = (props) => {
  const [anexosSelecionados, setAnexosSelecionados] = useState<AnexoType[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [verMaisAnexos, setVerMaisAnexos] = useState<boolean>(false);

  useEffect(() => {
    if (props.anexos) {
      setAnexosSelecionados(props.anexos);
    }
  }, [props.anexos]);

  const openFileInput = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!props.isVisualizacao) {
      e.stopPropagation();
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const renderUploadFile = () => {
    let className = "cloud-upload-input mb-1 ";
    if (!props.isVisualizacao) {
      className += "cursor-pointer";
    } else {
      className += "disabled";
    }

    return (
      <>
        <div className={className} onClick={openFileInput}>
          <FaCloudUploadAlt color={"#b2b4b5"} size={"2rem"} />
          <input
            ref={fileInputRef}
            type="file"
            id="cloud-upload-input"
            accept={props.accept || ""}
            onChange={adicionarAnexo}
            style={{ display: "none" }}
            value=""
          />
        </div>
      </>
    );
  };

  const adicionarAnexo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let stringArquivosFormatada = props.limiteDeAnexos && props.limiteDeAnexos > 1 ? "arquivos" : "arquivo";
    let msgErro = `É permitido anexar apenas ${props.limiteDeAnexos} ${stringArquivosFormatada}`;

    if (props.tamanhoMaximoEmMegaByteDoAnexo) {
      msgErro += ` com tamanho máximo de ${props.tamanhoMaximoEmMegaByteDoAnexo}MB cada.`;
    }

    if (props.limiteDeAnexos && anexosSelecionados.length >= props.limiteDeAnexos) {
      toast.error(msgErro);
      return;
    }
    const anexoSelecionado = e.target.files && e.target.files[0];
    if (anexoSelecionado) {
      if (props.tamanhoMaximoEmMegaByteDoAnexo && tamanhoMaximoDoAnexoExcedido(anexoSelecionado)) {
        toast.error(msgErro);
        return;
      }
      if (props.extensoesAceitas && extensaoDoArquivoNaoPermitida(anexoSelecionado)) {
        msgErro = props.mensagemDeErro
          ? props.mensagemDeErro
          : `É permitido anexar somente arquivos nos formatos: ${props.extensoesAceitas.join(", ")}`;
        toast.error(msgErro);
        return;
      }
      let anexosAtualizados = [...anexosSelecionados];
      props.onAddAnexo(anexoSelecionado);
    }
  };

  const extensaoDoArquivoNaoPermitida = (anexo: File): any => {
    if (props.extensoesAceitas) {
      let extensao = anexo.name.split(".").pop();
      if (extensao) {
        return !props.extensoesAceitas.includes(extensao);
      }
    }
    return false;
  };

  const tamanhoMaximoDoAnexoExcedido = (anexo: File) => {
    const tamanhoDoAnexoMb: number = transformarBytesEmMegaBytes(anexo.size);
    if (props.tamanhoMaximoEmMegaByteDoAnexo && props.tamanhoMaximoEmMegaByteDoAnexo < tamanhoDoAnexoMb) {
      return true;
    }
  };

  const transformarBytesEmMegaBytes = (tamanhoDoAnexoEmBytes: number): number => {
    const megabytes = tamanhoDoAnexoEmBytes / (1024 * 1024);
    return parseFloat(megabytes.toFixed(2));
  };

  const removerAnexo = (anexo: AnexoType, indexAnexo: number) => {
    props.onRemoveAnexo(anexo);
  };

  const renderArquivosSelecionados = () => {
    if (anexosSelecionados.length === 1) {
      return (
        <div>
          <div className="sub-titulo">Anexos</div>
          {renderAnexo(anexosSelecionados[0], 0)}
        </div>
      );
    } else {
      return (
        <div>
          {anexosSelecionados.length > 0 && <div className="sub-titulo">Anexos</div>}
          {!verMaisAnexos && anexosSelecionados.length > 1 && renderVerMaisAnexos()}
          {verMaisAnexos && renderListaDeAnexosSelecionados()}
          {anexosSelecionados.length > 1 && verMaisAnexos && renderVerMenosAnexos()}
        </div>
      );
    }
  };

  const renderVerMaisAnexos = (): ReactNode => {
    return (
      <div>
        {renderAnexo(anexosSelecionados[0], 0)}
        <div className="mt-2 cursor-pointer fit-content" onClick={() => setVerMaisAnexos(true)}>
          <span className="sub-titulo"> + Ver mais anexos</span>
        </div>
      </div>
    );
  };

  const renderVerMenosAnexos = () => {
    return (
      <div>
        <div className="mt-2 cursor-pointer fit-content" onClick={() => setVerMaisAnexos(false)}>
          <span className="sub-titulo"> - Ver menos anexos</span>
        </div>
      </div>
    );
  };

  const renderListaDeAnexosSelecionados = (): ReactNode => {
    return (
      <div>
        {anexosSelecionados.map((arquivo: AnexoType, index: number) => {
          return renderAnexo(arquivo, index);
        })}
      </div>
    );
  };

  const renderAnexo = (anexo: AnexoType, index: number): ReactNode => {
    return (
      <div key={`anexo-selecionado-${index}`}>
        <BsFileEarmark
          className="cursor-pointer me-2"
          size="0.9rem"
          onClick={() => props.onDownload && props.onDownload(anexo)}
        />
        <span>{anexo.nome}</span>
        {!props.isVisualizacao && (
          <FaTimes
            className="cursor-pointer ms-2"
            color="#e75458"
            size="0.9rem"
            onClick={(e) => removerAnexo(anexo, index)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div>
        {renderUploadFile()}
        {renderArquivosSelecionados()}
      </div>
    </>
  );
};

export default CloudAnexo;
